import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Sparkles, Loader2, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export function SustainabilityTab({
  sustainability, setSustainability,
  aiDrafts, saving, aiGenerating,
  saveSustainability, generateAiDraft,
}) {
  const checks = [
    { key: "control_plan_verified", label: "Control Plan Verified", desc: "Control plan has been reviewed and approved for ongoing use" },
    { key: "monitoring_active", label: "Monitoring Systems Active", desc: "SPC charts, dashboards, or alerts are live and functioning" },
    { key: "response_plan_verified", label: "Response Plan Verified", desc: "Out-of-control action plans are documented and understood" },
    { key: "ownership_handoff", label: "Ownership Handoff Complete", desc: "Process owner has accepted responsibility for ongoing performance" },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-lg p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-primary" />
            <h3 className="font-heading font-semibold">Sustainability Confirmation</h3>
          </div>
          <Button
            variant="outline" size="sm"
            onClick={() => generateAiDraft("sustainability_risks")}
            disabled={!!aiGenerating}
            className="gap-2"
            data-testid="ai-sustainability-risks-btn"
          >
            {aiGenerating === "sustainability_risks" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            AI Risk Assessment
          </Button>
        </div>

        <div className="space-y-3">
          {checks.map((check) => (
            <label key={check.key} className="flex items-start gap-3 p-4 bg-background border border-border rounded-lg cursor-pointer hover:border-primary/40 transition-colors" data-testid={`sustainability-${check.key}`}>
              <input
                type="checkbox"
                checked={!!sustainability[check.key]}
                onChange={(e) => setSustainability((prev) => ({ ...prev, [check.key]: e.target.checked }))}
                className="h-5 w-5 mt-0.5 rounded border-border accent-primary shrink-0"
              />
              <div>
                <p className="text-sm font-medium">{check.label}</p>
                <p className="text-xs text-muted-foreground">{check.desc}</p>
              </div>
            </label>
          ))}
        </div>

        <div className="space-y-2">
          <Label>Handoff Notes</Label>
          <Textarea
            placeholder="Document who is taking ownership, any transition steps, ongoing support arrangements..."
            value={sustainability.handoff_notes || ""}
            onChange={(e) => setSustainability((prev) => ({ ...prev, handoff_notes: e.target.value }))}
            rows={4}
            data-testid="handoff-notes-input"
          />
        </div>

        <Button onClick={saveSustainability} disabled={saving} data-testid="save-sustainability-btn">
          {saving ? "Saving..." : "Save Sustainability Confirmation"}
        </Button>
      </div>

      {/* AI Sustainability Risks */}
      {aiDrafts?.sustainability_risks && (
        <div className="bg-card border border-border rounded-lg p-6 space-y-4" data-testid="sustainability-risks-panel">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-semibold text-sm">AI: Sustainability Risk Assessment</h3>
            <span className={`text-xs font-semibold px-3 py-1 rounded-full ${(aiDrafts.sustainability_risks.sustainability_score ?? 0) >= 70 ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`} data-testid="sustainability-score-badge">
              Score: {aiDrafts.sustainability_risks.sustainability_score ?? "N/A"}%
            </span>
          </div>
          <p className="text-sm">{aiDrafts.sustainability_risks.overall_assessment}</p>
          {aiDrafts.sustainability_risks.risks?.map((r, i) => (
            <div key={i} className={`p-3 rounded-lg border text-xs ${r.likelihood === "high" ? "bg-red-500/10 border-red-500/20" : r.likelihood === "medium" ? "bg-amber-500/10 border-amber-500/20" : "bg-emerald-500/10 border-emerald-500/20"}`}>
              <div className="flex items-center gap-2">
                <AlertTriangle className={`w-3 h-3 ${r.likelihood === "high" ? "text-red-400" : r.likelihood === "medium" ? "text-amber-400" : "text-emerald-400"}`} />
                <span className="font-medium">{r.area}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${r.likelihood === "high" ? "bg-red-500/15 text-red-400" : r.likelihood === "medium" ? "bg-amber-500/15 text-amber-400" : "bg-emerald-500/15 text-emerald-400"}`}>{r.likelihood}</span>
              </div>
              <p className="mt-1">{r.risk}</p>
              <p className="text-muted-foreground mt-1">Mitigation: {r.mitigation}</p>
            </div>
          ))}
          {aiDrafts.sustainability_risks.handoff_gaps?.length > 0 && (
            <div className="pt-2 border-t border-border space-y-1">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Handoff Gaps</p>
              {aiDrafts.sustainability_risks.handoff_gaps.map((g, i) => (
                <p key={i} className="text-xs p-2 bg-red-500/10 border border-red-500/20 rounded">{g}</p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
